import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import './LandingPage.css';

const FEATURES = [
  {
    icon: '📅',
    title: 'Έξυπνο Ημερολόγιο',
    desc: 'Εβδομαδιαία και ημερήσια προβολή με drag-and-drop κρατήσεις. Διαχειρίσου τον χρόνο σου άμεσα.'
  },
  {
    icon: '👥',
    title: 'Διαχείριση Πελατών',
    desc: 'Πλήρες ιστορικό κρατήσεων ανά πελάτη, σημειώσεις και αναζήτηση με ένα κλικ.'
  },
  {
    icon: '✂️',
    title: 'Υπηρεσίες & Τιμοκατάλογος',
    desc: 'Δημιούργησε υπηρεσίες με διάρκεια, τιμή και χρώμα. Εμφανίζονται αυτόματα στις κρατήσεις.'
  },
  {
    icon: '👤',
    title: 'Πολλαπλοί Υπάλληλοι',
    desc: 'Ξεχωριστό χρώμα ανά υπάλληλο, φιλτράρισμα στο ημερολόγιο και στατιστικά αποδοτικότητας.'
  },
  {
    icon: '📊',
    title: 'Στατιστικά & Έσοδα',
    desc: 'Γραφήματα κρατήσεων, εσόδων, δημοφιλέστερων υπηρεσιών και κατανομής ανά ημέρα/ώρα.'
  },
  {
    icon: '🤖',
    title: 'AI Voice Booking',
    desc: 'Ενσωμάτωση με AI φωνητικό σύστημα. Αυτόματη καταχώρηση κρατήσεων χωρίς χειροκίνητη εισαγωγή.'
  },
];

const PLANS = [
  {
    name: 'Starter',
    price: '29',
    period: '/μήνα',
    desc: 'Για νέα κομμωτήρια',
    features: ['1 υπάλληλος', 'Απεριόριστες κρατήσεις', 'Ημερολόγιο & πελάτες', 'Στατιστικά βασικά', 'Email υποστήριξη'],
    cta: 'Ξεκίνα δωρεάν',
    highlight: false,
  },
  {
    name: 'Pro',
    price: '59',
    period: '/μήνα',
    desc: 'Για επαγγελματίες',
    features: ['Έως 5 υπάλληλοι', 'Απεριόριστες κρατήσεις', 'Πλήρη στατιστικά', 'SMS υπενθυμίσεις', 'Προτεραιότητα υποστήριξης', 'AI booking'],
    cta: 'Δοκίμασε 14 μέρες',
    highlight: true,
    badge: 'Δημοφιλές'
  },
  {
    name: 'Business',
    price: '99',
    period: '/μήνα',
    desc: 'Για μεγάλα σαλόνια',
    features: ['Απεριόριστοι υπάλληλοι', 'Multi-location', 'API πρόσβαση', 'Custom integrations', 'Dedicated support', 'White label'],
    cta: 'Επικοινωνία',
    highlight: false,
  },
];

const TESTIMONIALS = [
  { name: 'Μαρία Αντωνίου', role: 'Ιδιοκτήτρια κομμωτηρίου, Αθήνα', text: 'Εξοικονομώ 2 ώρες κάθε μέρα που πριν τις έχανα στο τηλέφωνο. Οι πελάτες μου εντυπωσιάζονται!', avatar: 'Μ' },
  { name: 'Νίκος Παπαδόπουλος', role: 'Barber shop, Θεσσαλονίκη', text: 'Το ημερολόγιο είναι απλό και γρήγορο. Εδώ και 6 μήνες δεν έχω χάσει ούτε μία κράτηση.', avatar: 'Ν' },
  { name: 'Ελένη Κωστάκη', role: 'Beauty salon, Πάτρα', text: 'Τα στατιστικά με βοήθησαν να δω ποιες υπηρεσίες φέρνουν τα περισσότερα έσοδα. Αύξησα τζίρο 30%!', avatar: 'Ε' },
];

export default function LandingPage() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const goTrial = () => navigate('/trial');

  return (
    <div className="lp-root">

      {/* ── HEADER ── */}
      <header className={`lp-header ${scrolled ? 'lp-header-scrolled' : ''}`}>
        <div className="lp-header-inner">
          <div className="lp-logo">
            <span className="lp-logo-icon">✂</span>
            <span className="lp-logo-text">SalonBook</span>
          </div>

          <nav className={`lp-nav ${menuOpen ? 'lp-nav-open' : ''}`}>
            <button onClick={() => scrollTo('features')}>Χαρακτηριστικά</button>
            <button onClick={() => scrollTo('pricing')}>Τιμολόγηση</button>
            <button onClick={() => scrollTo('testimonials')}>Κριτικές</button>
            <button onClick={() => scrollTo('contact')}>Επικοινωνία</button>
          </nav>

          <div className="lp-header-actions">
            <button className="lp-btn-login" onClick={login}>
              Είσοδος επαγγελματία
            </button>
            <button className="lp-btn-primary" onClick={goTrial}>
              Δωρεάν δοκιμή
            </button>
          </div>

          <button className="lp-hamburger" onClick={() => setMenuOpen(o => !o)}>
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Mobile nav */}
        {menuOpen && (
          <div className="lp-mobile-nav">
            <button onClick={() => scrollTo('features')}>Χαρακτηριστικά</button>
            <button onClick={() => scrollTo('pricing')}>Τιμολόγηση</button>
            <button onClick={() => scrollTo('testimonials')}>Κριτικές</button>
            <button onClick={() => scrollTo('contact')}>Επικοινωνία</button>
            <div className="lp-mobile-nav-divider" />
            <button className="lp-btn-login-mobile" onClick={login}>Είσοδος</button>
            <button className="lp-btn-primary-mobile" onClick={goTrial}>Δωρεάν δοκιμή →</button>
          </div>
        )}
      </header>

      {/* ── HERO ── */}
      <div className="lp-hero-wrapper">
        <div className="lp-hero-bg">
          <div className="lp-hero-blob lp-hero-blob-1" />
          <div className="lp-hero-blob lp-hero-blob-2" />
          <div className="lp-hero-grid" />
        </div>
        <section className="lp-hero" ref={heroRef}>
          <div className="lp-hero-inner">
            <div className="lp-hero-badge">🚀 Νέο: AI φωνητική κράτηση</div>
            <h1 className="lp-hero-title">
              Το ημερολόγιο που<br />
              <span className="lp-hero-accent">αξίζει το κομμωτήριό σου</span>
            </h1>
            <p className="lp-hero-sub">
              Διαχειρίσου κρατήσεις, πελάτες, υπαλλήλους και έσοδα
              από μία επαγγελματική πλατφόρμα. Χωρίς χαρτιά, χωρίς άγχος.
            </p>
            <div className="lp-hero-ctas">
              <button className="lp-btn-hero-pri" onClick={goTrial}>
                Ξεκίνα δωρεάν — 14 μέρες
              </button>
              <button className="lp-btn-hero-sec" onClick={() => scrollTo('features')}>
                Δες πώς λειτουργεί ↓
              </button>
            </div>
            <div className="lp-hero-stats">
              <div className="lp-hero-stat"><span>500+</span>Κομμωτήρια</div>
              <div className="lp-hero-stat-divider" />
              <div className="lp-hero-stat"><span>50,000+</span>Κρατήσεις/μήνα</div>
              <div className="lp-hero-stat-divider" />
              <div className="lp-hero-stat"><span>4.9★</span>Αξιολόγηση</div>
            </div>
          </div>

          {/* Mockup */}
          <div className="lp-hero-mockup">
            <div className="lp-mockup-window">
              <div className="lp-mockup-bar">
                <span /><span /><span />
                <div className="lp-mockup-url">salonbook.gr/app</div>
              </div>
              <div className="lp-mockup-body">
                <div className="lp-mockup-sidebar">
                  {['📅','📋','👥','✂️','📊','⚙️'].map((ic, i) => (
                    <div key={i} className={`lp-mockup-nav-item ${i === 0 ? 'active' : ''}`}>{ic}</div>
                  ))}
                </div>
                <div className="lp-mockup-cal">
                  <div className="lp-mockup-cal-header">
                    {['ΔΕΥ','ΤΡΙ','ΤΕΤ','ΠΕΜ','ΠΑΡ'].map((d, i) => (
                      <div key={i} className="lp-mockup-day-label">{d}</div>
                    ))}
                  </div>
                  {[
                    { day: 0, top: 20, h: 40, name: 'Μαρία Π.', svc: 'Βαφή',       color: '#534AB7' },
                    { day: 1, top: 10, h: 28, name: 'Νίκος Κ.', svc: 'Κόψιμο',     color: '#E91E63' },
                    { day: 2, top: 35, h: 52, name: 'Ελένη Σ.', svc: 'Χτένισμα',   color: '#2ECC71' },
                    { day: 3, top: 15, h: 36, name: 'Δήμητρα Λ.', svc: 'Βαφή',     color: '#F39C12' },
                    { day: 4, top: 28, h: 44, name: 'Κώστας Ρ.', svc: 'Κόψιμο',    color: '#3498DB' },
                    { day: 1, top: 52, h: 30, name: 'Σοφία Α.',  svc: 'Περιποίηση', color: '#9B59B6' },
                  ].map((b, i) => (
                    <div key={i} className="lp-mockup-chip" style={{
                      left:            `calc(${b.day * 20}% + 4px)`,
                      top:             `${b.top}%`,
                      height:          `${b.h}px`,
                      borderLeftColor: b.color,
                      background:      b.color + '18',
                    }}>
                      <div className="lp-mockup-chip-name" style={{ color: b.color }}>{b.name}</div>
                      <div className="lp-mockup-chip-svc">{b.svc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ── FEATURES ── */}
      <section id="features" className="lp-section lp-features">
        <div className="lp-section-inner">
          <div className="lp-section-label">Χαρακτηριστικά</div>
          <h2 className="lp-section-title">Όλα όσα χρειάζεσαι<br />σε μία πλατφόρμα</h2>
          <p className="lp-section-sub">Από την πρώτη κράτηση ως την ανάλυση εσόδων — χωρίς πολυπλοκότητα.</p>

          <div className="lp-features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="lp-feature-card">
                <div className="lp-feature-icon">{f.icon}</div>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="lp-section lp-testimonials">
        <div className="lp-section-inner">
          <div className="lp-section-label">Κριτικές</div>
          <h2 className="lp-section-title">Τι λένε οι επαγγελματίες</h2>
          <div className="lp-testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="lp-testimonial-card">
                <div className="lp-testimonial-stars">★★★★★</div>
                <p className="lp-testimonial-text">"{t.text}"</p>
                <div className="lp-testimonial-author">
                  <div className="lp-testimonial-avatar">{t.avatar}</div>
                  <div>
                    <div className="lp-testimonial-name">{t.name}</div>
                    <div className="lp-testimonial-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="lp-section lp-pricing">
        <div className="lp-section-inner">
          <div className="lp-section-label">Τιμολόγηση</div>
          <h2 className="lp-section-title">Απλές & διαφανείς τιμές</h2>
          <p className="lp-section-sub">14 μέρες δωρεάν δοκιμή. Χωρίς πιστωτική κάρτα.</p>

          <div className="lp-pricing-grid">
            {PLANS.map((p, i) => (
              <div key={i} className={`lp-plan-card ${p.highlight ? 'lp-plan-highlight' : ''}`}>
                {p.badge && <div className="lp-plan-badge">{p.badge}</div>}
                <div className="lp-plan-name">{p.name}</div>
                <div className="lp-plan-price">
                  <span className="lp-plan-currency">€</span>
                  <span className="lp-plan-amount">{p.price}</span>
                  <span className="lp-plan-period">{p.period}</span>
                </div>
                <div className="lp-plan-desc">{p.desc}</div>
                <ul className="lp-plan-features">
                  {p.features.map((f, j) => (
                    <li key={j}><span className="lp-plan-check">✓</span>{f}</li>
                  ))}
                </ul>
                <button
                  className={`lp-plan-cta ${p.highlight ? 'lp-plan-cta-hi' : ''}`}
                  onClick={goTrial}>
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="lp-cta-banner">
        <div className="lp-cta-banner-inner">
          <h2>Έτοιμος να αναβαθμίσεις το κομμωτήριό σου;</h2>
          <p>Εγγραφή σε 2 λεπτά. Χωρίς συμβόλαιο, χωρίς κρυφές χρεώσεις.</p>
          <button className="lp-btn-hero-pri" onClick={goTrial}>
            Ξεκίνα σήμερα — δωρεάν
          </button>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer id="contact" className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <div className="lp-footer-logo">
              <span className="lp-logo-icon">✂</span>
              <span className="lp-logo-text">SalonBook</span>
            </div>
            <p className="lp-footer-tagline">
              Η επαγγελματική πλατφόρμα για κομμωτήρια & beauty salons.
            </p>
            <div className="lp-footer-contact">
              <a href="mailto:info@salonbook.gr">info@salonbook.gr</a>
              <span>·</span>
              <a href="tel:+302101234567">+30 210 123 4567</a>
            </div>
          </div>

          <div className="lp-footer-links">
            <div className="lp-footer-col">
              <div className="lp-footer-col-title">Προϊόν</div>
              <button onClick={() => scrollTo('features')}>Χαρακτηριστικά</button>
              <button onClick={() => scrollTo('pricing')}>Τιμολόγηση</button>
              <button onClick={() => scrollTo('testimonials')}>Κριτικές</button>
              <button onClick={login}>Σύνδεση</button>
            </div>
            <div className="lp-footer-col">
              <div className="lp-footer-col-title">Υποστήριξη</div>
              <a href="mailto:support@salonbook.gr">Email υποστήριξης</a>
              <a href="/faq">FAQ</a>
              <a href="/docs">Οδηγός χρήσης</a>
            </div>
            <div className="lp-footer-col">
              <div className="lp-footer-col-title">Νομικά</div>
              <a href="/privacy">Πολιτική απορρήτου</a>
              <a href="/terms">Όροι χρήσης</a>
              <a href="/gdpr">GDPR</a>
            </div>
          </div>
        </div>

        <div className="lp-footer-bottom">
          <span>© {new Date().getFullYear()} SalonBook. Όλα τα δικαιώματα διατηρούνται.</span>
          <span>Made with ❤️ in Greece 🇬🇷</span>
        </div>
      </footer>
    </div>
  );
}